import { zodResolver } from "@hookform/resolvers/zod";
import { Accordion, AccordionDetails, AccordionSummary, FormHelperText, Grid } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { sentenceCase } from "change-case";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import WYSIWYG from "components/WYSIWYG/WYSIWYG";
import {
  proposalContentTypeName,
  proposalContentTypeTooltip,
} from "constants/proposalSettingsDisplayType";
import {
  Exact,
  OrganizationProposalContent,
  ProposalContentType,
  UpdateOrganizationProposalContentMutationVariables,
} from "generated/graphql";
import { Controller, useForm } from "react-hook-form";
import { useState } from "react";
import * as z from "zod";

const FormInputWYSIWYG: React.FC<{
  type: string;
  content: OrganizationProposalContent;
  loading: boolean;
  onSubmit: (
    values: Exact<{
      content: string;
      type: ProposalContentType;
    }>
  ) => void;
}> = ({ type, content, loading, onSubmit }) => {
  const [expanded, setExpanded] = useState(false);
  const getWysiwygType = (contentType: string) => {
    const upperType = contentType.toUpperCase();
    if (upperType === "PROJECT_SUMMARY") return "proposalStage";
    if (upperType === "INVOICE_DETAILS") return "invoice";
    return "proposal";
  };

  const methods = useForm<UpdateOrganizationProposalContentMutationVariables>({
    resolver: zodResolver(
      z.object({
        content: z
          .string({ invalid_type_error: "Field cannot be empty" })
          .min(1, "Field cannot be empty"),
      })
    ),
    defaultValues: { content: "" },
  });

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  const getTitle = () => {
    const upperType = content.type.toUpperCase();
    return proposalContentTypeName[upperType] || sentenceCase(content.type);
  };

  return (
    <Accordion
      key={type}
      expanded={expanded}
      onChange={handleToggle}
      elevation={1}
      sx={{ mb: "8px" }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <MDTypography variant="h6" fontWeight="medium">
          {getTitle()}
        </MDTypography>
      </AccordionSummary>
      <AccordionDetails>
        <MDBox
          role="form"
          component="form"
          onSubmit={methods.handleSubmit(
            async ({ ...rest }: UpdateOrganizationProposalContentMutationVariables) => {
              await onSubmit({ ...rest, type: content.type as ProposalContentType });
              methods.reset();
            }
          )}
        >
          <Controller
            control={methods.control}
            name="content"
            render={({ field, fieldState: { error } }) => (
              <>
                <WYSIWYG
                  type={getWysiwygType(content.type)}
                  content={content.content}
                  onChange={(content) => field.onChange(content)}
                  title={proposalContentTypeName[content.type.toUpperCase()]}
                  tooltip={proposalContentTypeTooltip[content.type.toUpperCase()]}
                />
                {error && error?.message && (
                  <FormHelperText
                    sx={{
                      color: "red",
                    }}
                  >
                    {error.message}
                  </FormHelperText>
                )}
              </>
            )}
          />
          <Grid item xs={12} lg={6}>
            <MDBox display="flex" justifyContent="flex-end" mt={2}>
              <MDButton variant="gradient" color="success" type="submit" disabled={loading}>
                Save
              </MDButton>
            </MDBox>
          </Grid>
        </MDBox>
      </AccordionDetails>
    </Accordion>
  );
};

export default FormInputWYSIWYG;
